/**
 * Lasting Grass core runtime: window.LG.
 * Owner: foundation. Area agents must not edit this file; ask for changes in
 * their requests output.
 *
 * Loaded with defer from <head> (snippets/lg-head-assets.liquid), so it runs
 * before any feature script a section loads with defer further down the page.
 * Vanilla ES2020, no libraries. Nothing here is needed to show page content:
 * every section renders its primary content on the server.
 *
 * Cart calls use Shopify's Ajax Cart API (/cart.js, /cart/add.js,
 * /cart/change.js, /cart/update.js). Every cart call resolves, it never
 * throws, and resolves to a typed result:
 *
 *   success  { ok: true,  cart: Cart, ...extras }
 *   failure  { ok: false, status: number, message: string, description: string }
 *
 * status is the HTTP status. It is 0 when the request never reached Shopify
 * (network failure, or the arguments were rejected before sending).
 *
 * After any successful cart change the header badge is refreshed and a
 * CustomEvent "lg:cart-updated" is dispatched on document:
 *   detail = { cart: Cart, count: number, source: string }
 * where source is "add", "change", "update" or "refresh".
 */
(function () {
  "use strict";

  if (window.LG && window.LG.version) return;

  /** @typedef {{ id: number, key: string, quantity: number, title: string, price: number, final_line_price: number, properties: Object|null }} CartLine */
  /** @typedef {{ token: string, note: string|null, attributes: Object, item_count: number, items: CartLine[], total_price: number, original_total_price: number, currency: string }} Cart */
  /** @typedef {{ ok: true, cart: Cart }} CartOk */
  /** @typedef {{ ok: false, status: number, message: string, description: string }} CartFail */

  // ---------------------------------------------------------------------------
  // config (written by snippets/lg-head-assets.liquid as JSON)
  // ---------------------------------------------------------------------------

  var DEFAULTS = {
    currency: "AUD",
    locale: "en-AU",
    cartCount: 0,
    routes: {
      root: "/",
      cart: "/cart",
      cartAdd: "/cart/add",
      cartChange: "/cart/change",
      cartUpdate: "/cart/update",
    },
    strings: {
      cartLabelOne: "Cart, __count__ item",
      cartLabelOther: "Cart, __count__ items",
      saving: "Saving",
    },
  };

  function readConfig() {
    var cfg = {
      currency: DEFAULTS.currency,
      locale: DEFAULTS.locale,
      cartCount: DEFAULTS.cartCount,
      routes: Object.assign({}, DEFAULTS.routes),
      strings: Object.assign({}, DEFAULTS.strings),
    };
    try {
      var el = document.getElementById("lg-config");
      if (!el) return cfg;
      var parsed = JSON.parse(el.textContent);
      if (parsed && typeof parsed === "object") {
        if (parsed.currency) cfg.currency = String(parsed.currency);
        if (parsed.locale) cfg.locale = String(parsed.locale);
        if (typeof parsed.cartCount === "number") cfg.cartCount = parsed.cartCount;
        Object.assign(cfg.routes, parsed.routes || {});
        Object.assign(cfg.strings, parsed.strings || {});
      }
    } catch (e) {
      /* fall back to defaults */
    }
    return cfg;
  }

  var config = readConfig();

  // ---------------------------------------------------------------------------
  // helpers
  // ---------------------------------------------------------------------------

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  var money = null;
  try {
    money = new Intl.NumberFormat(config.locale, { style: "currency", currency: config.currency });
  } catch (e) {
    money = null;
  }

  /**
   * Money is integer cents everywhere (as Shopify returns it). Formats like the
   * Next.js site's formatAud(): en-AU, AUD, "$1,634.35".
   * @param {number|string} cents
   * @returns {string} empty string when cents is not a finite number
   */
  function formatMoney(cents) {
    var n = Number(cents);
    if (!Number.isFinite(n)) return "";
    var value = n / 100;
    return money ? money.format(value) : "$" + value.toFixed(2);
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /** @returns {CartFail} */
  function fail(status, message, description) {
    return { ok: false, status: status, message: message, description: description || "" };
  }

  /**
   * One fetch, never throws. Resolves to { ok: true, data } or a CartFail.
   */
  async function request(url, init) {
    var options = Object.assign(
      { credentials: "same-origin", headers: { Accept: "application/json" } },
      init || {}
    );
    var res;
    try {
      res = await fetch(url, options);
    } catch (e) {
      return fail(0, "Network error", e && e.message ? String(e.message) : "The request could not be sent.");
    }
    var data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok) {
      var message = "Request failed";
      var description = "";
      if (data && typeof data === "object") {
        message = data.message || data.error || res.statusText || message;
        var d = data.description || data.errors || "";
        description = typeof d === "string" ? d : JSON.stringify(d);
      } else if (res.statusText) {
        message = res.statusText;
      }
      return fail(res.status, String(message), description);
    }
    return { ok: true, data: data };
  }

  function postJson(url, body) {
    return request(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // ---------------------------------------------------------------------------
  // header badge
  // ---------------------------------------------------------------------------

  function cartLabel(count) {
    var tpl = count === 1 ? config.strings.cartLabelOne : config.strings.cartLabelOther;
    return String(tpl).replace("__count__", String(count)).replace("{count}", String(count));
  }

  /**
   * Sync every [data-lg-cart-badge] and [data-lg-cart-link] with a count. The
   * header renders the same values on the server (99+ cap included).
   * @param {number} count
   */
  function refreshHeader(count) {
    var n = Math.max(0, Number(count) || 0);
    document.querySelectorAll("[data-lg-cart-badge]").forEach(function (el) {
      el.textContent = n > 99 ? "99+" : String(n);
      el.hidden = n < 1;
    });
    document.querySelectorAll("[data-lg-cart-link]").forEach(function (el) {
      el.setAttribute("aria-label", cartLabel(n));
    });
  }

  // ---------------------------------------------------------------------------
  // cart
  // ---------------------------------------------------------------------------

  function cartUrl(route) {
    return route + ".js";
  }

  /**
   * Fetch the cart, sync the header badge. Does not dispatch lg:cart-updated
   * (use refresh() for that).
   * @returns {Promise<CartOk|CartFail>}
   */
  async function getCart() {
    var res = await request(cartUrl(config.routes.cart), { cache: "no-store" });
    if (!res.ok) return res;
    refreshHeader(res.data.item_count);
    return { ok: true, cart: res.data };
  }

  function announce(cart, source) {
    refreshHeader(cart.item_count);
    document.dispatchEvent(
      new CustomEvent("lg:cart-updated", {
        detail: { cart: cart, count: cart.item_count, source: source },
      })
    );
  }

  /**
   * getCart() plus the lg:cart-updated event with source "refresh". Called
   * when a page is restored from the back/forward cache, and by any code that
   * knows the cart changed elsewhere.
   * @returns {Promise<CartOk|CartFail>}
   */
  async function refresh() {
    var res = await getCart();
    if (res.ok) announce(res.cart, "refresh");
    return res;
  }

  /**
   * Add one variant.
   * @param {{ id: number|string, quantity?: number, properties?: Object<string,string> }} input
   *   id is the variant id. properties become line item properties, for example
   *   { Colour: "Green" } for Coolplay Green. Empty and null values are dropped.
   * @returns {Promise<(CartOk & { items: CartLine[] })|CartFail>}
   */
  async function addToCart(input) {
    var id = input && Number(input.id);
    var quantity = input && input.quantity != null ? Number(input.quantity) : 1;
    if (!Number.isInteger(id) || id <= 0) return fail(0, "Invalid variant", "addToCart needs a numeric variant id.");
    if (!Number.isInteger(quantity) || quantity < 1) return fail(0, "Invalid quantity", "Quantity must be a whole number of 1 or more.");

    var item = { id: id, quantity: quantity };
    var props = {};
    var hasProps = false;
    if (input.properties && typeof input.properties === "object") {
      Object.keys(input.properties).forEach(function (key) {
        var v = input.properties[key];
        if (v !== null && v !== undefined && String(v) !== "") {
          props[key] = String(v);
          hasProps = true;
        }
      });
    }
    if (hasProps) item.properties = props;

    var added = await postJson(cartUrl(config.routes.cartAdd), { items: [item] });
    if (!added.ok) return added;

    var cartRes = await getCart();
    if (!cartRes.ok) return cartRes;
    announce(cartRes.cart, "add");
    return { ok: true, cart: cartRes.cart, items: (added.data && added.data.items) || [] };
  }

  /**
   * Set the quantity of one cart line. line is 1 based, as in /cart/change.js.
   * Quantity 0 removes the line.
   * @param {number} line
   * @param {number} quantity
   * @returns {Promise<CartOk|CartFail>}
   */
  async function changeLine(line, quantity) {
    var l = Number(line);
    var q = Number(quantity);
    if (!Number.isInteger(l) || l < 1) return fail(0, "Invalid line", "changeLine needs a line number of 1 or more.");
    if (!Number.isInteger(q) || q < 0) return fail(0, "Invalid quantity", "Quantity must be a whole number of 0 or more.");
    var res = await postJson(cartUrl(config.routes.cartChange), { line: l, quantity: q });
    if (!res.ok) return res;
    announce(res.data, "change");
    return { ok: true, cart: res.data };
  }

  /**
   * Update the cart note and/or cart attributes.
   * @param {{ note?: string, attributes?: Object<string,string> }} input
   * @returns {Promise<CartOk|CartFail>}
   */
  async function updateCart(input) {
    var body = {};
    if (input && typeof input.note === "string") body.note = input.note;
    if (input && input.attributes && typeof input.attributes === "object") body.attributes = input.attributes;
    if (!Object.keys(body).length) return fail(0, "Nothing to update", "updateCart needs a note or attributes.");
    var res = await postJson(cartUrl(config.routes.cartUpdate), body);
    if (!res.ok) return res;
    announce(res.data, "update");
    return { ok: true, cart: res.data };
  }

  /**
   * Subscribe to cart changes. The callback receives (cart, detail).
   * @param {(cart: Cart, detail: { cart: Cart, count: number, source: string }) => void} cb
   * @returns {() => void} unsubscribe
   */
  function onCartChange(cb) {
    if (typeof cb !== "function") return function () {};
    function handler(event) {
      var d = event.detail || {};
      cb(d.cart, d);
    }
    document.addEventListener("lg:cart-updated", handler);
    return function () {
      document.removeEventListener("lg:cart-updated", handler);
    };
  }

  // ---------------------------------------------------------------------------
  // email capture enhancements (snippets/lg-email-capture.liquid)
  // ---------------------------------------------------------------------------

  function initEmailCapture() {
    var forms = Array.prototype.slice.call(document.querySelectorAll("form[data-lg-capture]"));
    var hash = window.location.hash ? window.location.hash.slice(1) : "";

    forms.forEach(function (form) {
      var done = form.querySelector("[data-lg-capture-done]");
      var fields = form.querySelector("[data-lg-capture-fields]");
      // Remember the button's own label so a bfcache restore can put it back
      // (see the pageshow listener in the boot section).
      var idleButton = form.querySelector("[data-lg-capture-submit]");
      if (idleButton && !idleButton.dataset.idleLabel) {
        idleButton.dataset.idleLabel = idleButton.textContent.trim();
      }

      // Shopify marks every customer form on the page as posted. When there
      // are several and the URL hash names another one, show this one normally.
      if (done && !done.hidden && forms.length > 1 && hash && hash !== form.id) {
        done.hidden = true;
        if (fields) fields.hidden = false;
      }

      form.addEventListener("submit", function (event) {
        var honeypot = form.querySelector('input[name="website"]');
        if (honeypot && honeypot.value) {
          event.preventDefault();
          return;
        }
        var button = form.querySelector("[data-lg-capture-submit]");
        if (button) {
          // Defer the disable so the browser still submits the button's form.
          window.setTimeout(function () {
            button.disabled = true;
            button.textContent = config.strings.saving;
          }, 0);
        }
      });
    });

    // The submit button disappears on success; move focus to the confirmation
    // so keyboard and screen reader users are not dropped at the top of the page.
    var visibleDone = forms
      .map(function (f) {
        return f.querySelector("[data-lg-capture-done]");
      })
      .filter(function (el) {
        return el && !el.hidden;
      })[0];
    if (visibleDone) visibleDone.focus();
  }

  // ---------------------------------------------------------------------------
  // boot
  // ---------------------------------------------------------------------------

  window.addEventListener("pageshow", function (event) {
    // Restored from the back/forward cache: the server rendered badge is stale,
    // and a capture form that was submitted still shows its disabled "Saving"
    // button (the visitor pressed Back from Shopify's challenge or error page).
    if (!event.persisted) return;
    document.querySelectorAll("[data-lg-capture-submit]").forEach(function (button) {
      button.disabled = false;
      if (button.dataset.idleLabel) button.textContent = button.dataset.idleLabel;
    });
    refresh();
  });

  ready(initEmailCapture);

  window.LG = {
    version: "1.0.0",
    config: config,
    ready: ready,
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    getCart: getCart,
    addToCart: addToCart,
    changeLine: changeLine,
    updateCart: updateCart,
    onCartChange: onCartChange,
    refresh: refresh,
    refreshHeader: refreshHeader,
  };
})();
